import { type } from 'arktype';
import { createPaginatedResponseSchema } from '../shared/pagination';

/**
 * Contrato del catálogo `product` (tienda de recompensas).
 *
 * Reglas de dominio:
 * - El dinero se guarda siempre en **céntimos enteros**, nunca en decimales: `priceCents`
 *   es un entero (1050 = 10,50 €), no un `number` con coma flotante.
 * - `stock` nulo significa **«sin límite»**, no «cero»: `0` es agotado, `null` es
 *   ilimitado. Confundirlos vende infinito o nada.
 */
export const productResponseSchema = type({
	createdAt: 'string.date.iso',
	description: 'string | null',
	id: 'string.uuid',
	imageUrl: 'string | null',
	isActive: 'boolean',
	name: 'string',
	priceCents: 'number.integer',
	runaCost: 'number.integer | null',
	stock: 'number.integer | null',
	updatedAt: 'string.date.iso',
}).onUndeclaredKey('delete');

export type ProductResponse = typeof productResponseSchema.infer;

export const createProductSchema = type({
	'description?': 'string',
	'imageUrl?': 'string',
	// Por defecto `true`: un producto recién creado está a la venta.
	isActive: 'boolean = true',
	name: 'string',
	// Por defecto `0` céntimos.
	priceCents: 'number.integer = 0',
	'runaCost?': 'number.integer',
	// Ausente = sin límite (no confundir con `0`, que es agotado).
	'stock?': 'number.integer',
}).onUndeclaredKey('delete');

export type CreateProduct = typeof createProductSchema.infer;

// No se deriva con `.partial()` de `createProductSchema`: esta tiene `priceCents`/`isActive`
// con valor por defecto, y `.partial()` seguiría rellenando ese valor por defecto en una
// actualización que no toca esos campos — lo que reescribiría datos existentes sin que el
// cliente lo pidiera. Se declara la forma parcial a mano, sin defaults.
export const updateProductSchema = type({
	'description?': 'string',
	'imageUrl?': 'string',
	'isActive?': 'boolean',
	'name?': 'string',
	'priceCents?': 'number.integer',
	'runaCost?': 'number.integer',
	'stock?': 'number.integer',
}).onUndeclaredKey('delete');

export type UpdateProduct = typeof updateProductSchema.infer;

export const productPaginatedResponseSchema = createPaginatedResponseSchema(productResponseSchema);

export type ProductPaginatedResponse = typeof productPaginatedResponseSchema.infer;
